import * as React from 'react';

import { prefersReducedMotion, useTransitionStageDriver } from './transitionStageDriver';

/**
 * Where a value-to-value transition is. `pre` renders the outgoing value's
 * styles for one frame so the browser has a start state; `active` wears the
 * target's; `settled` is at rest.
 */
export type ValueTransitionStage = 'pre' | 'active' | 'settled';

/** {@link useTransitionedValue}'s internal state, exported for the pure reducers below. */
export interface IValueTransitionState<T> {
  /** The value to render toward. */
  value: T;
  /** The value being left; null once settled. */
  from: T | null;
  stage: ValueTransitionStage;
  /** Counts every change, so a retarget mid-flight re-arms the settle timer. */
  generation: number;
}

/**
 * The value changed. At rest the change stages through `pre`; mid-flight it
 * retargets straight to `active`, because the element's current pixel value is
 * already the start state and CSS transitions from wherever it is.
 */
export const valueOnChange = <T>(state: IValueTransitionState<T>, value: T): IValueTransitionState<T> => ({
  value,
  from: state.value,
  stage: state.stage === 'settled' ? 'pre' : 'active',
  generation: state.generation + 1,
});

/** The pre → active flip the double-rAF drives. Anything else is left for {@link valueOnSettle}. */
export const valueOnFrame = <T>(state: IValueTransitionState<T>): IValueTransitionState<T> =>
  state.stage === 'pre' ? { ...state, stage: 'active' } : state;

/**
 * A running transition finished (or the fallback timer fired). Also settles a
 * `pre` stage whose rAF never ran (throttled background tab) so the element
 * cannot get stuck between values.
 */
export const valueOnSettle = <T>(state: IValueTransitionState<T>): IValueTransitionState<T> =>
  state.stage === 'settled' ? state : { ...state, from: null, stage: 'settled' };

interface ITransitionedValueOptions {
  /** The CSS property whose `transitionend` settles the transition — see `useTransitionStageDriver`. */
  transitionProperty: string;
  /** The CSS transition's duration. The fallback timer fires this plus a margin after each stage starts. */
  durationMs: number;
}

export interface ITransitionedValue<T> {
  /** The value to render toward. */
  value: T;
  /** The value being left, for the duration of the transition; null at rest. */
  from: T | null;
  /** Which styles the element should wear this frame. */
  stage: ValueTransitionStage;
  /** Attach to the transitioning element itself; settles the transition. */
  onTransitionEnd: React.TransitionEventHandler;
}

/**
 * Stages a CSS transition between two values of the same thing — a card that
 * grows from one presentation to the next — and tells the element which stage
 * it is in.
 *
 * The caller maps stages to styles: the outgoing value's styles on `pre`, the
 * target's on `active` and `settled`. A first render starts at `settled` with
 * no animation, a change mid-flight retargets from wherever the transition
 * currently is, and a `prefers-reduced-motion` viewer gets the new value with
 * no staging at all.
 *
 * Change detection runs during render rather than in an effect, so the commit
 * that first carries the new value already carries its `pre` stage — a change
 * that arrives outside a click (rows landing from a query) never paints one
 * at-rest frame of the target before the transition starts.
 *
 * @param value The value to render toward; every change stages a transition.
 * @param options.transitionProperty The property whose `transitionend`, on the element itself, settles the transition.
 * @param options.durationMs The CSS transition's duration; the fallback timer fires this plus a margin.
 * @returns The target value, the value being left (null at rest), the stage,
 *   and the `transitionend` handler for the transitioning element.
 */
export const useTransitionedValue = <T>(
  value: T,
  { transitionProperty, durationMs }: ITransitionedValueOptions
): ITransitionedValue<T> => {
  const [state, setState] = React.useState<IValueTransitionState<T>>({
    value,
    from: null,
    stage: 'settled',
    generation: 0,
  });

  if (state.value !== value) {
    setState(
      prefersReducedMotion()
        ? { value, from: null, stage: 'settled', generation: state.generation + 1 }
        : valueOnChange(state, value)
    );
  }

  const onTransitionEnd = useTransitionStageDriver({
    isTransitioning: state.stage !== 'settled',
    stageKey: `${state.stage}:${state.generation}`,
    durationMs,
    transitionProperty,
    onFrame: () => setState(valueOnFrame),
    onSettle: () => setState(valueOnSettle),
  });

  return { value: state.value, from: state.from, stage: state.stage, onTransitionEnd };
};
